<script setup lang="ts">
import { ref, watch } from "vue";

import type { LoadedWorker } from "./types";

import Avatar, { AvatarSize } from "@/dsComponents/Avatar";
import Dropdown, { DropdownItem } from "@/dsComponents/Dropdown";
import DropdownInput from "@/dsComponents/Dropdown/DropdownInput.vue";

interface WorkerDropdownProps {
    workers: LoadedWorker[];
    modelValue: string;
    loading?: boolean;
    totalItems: number;
}

const props = defineProps<WorkerDropdownProps>();

const emit = defineEmits<{
    (event: "select", id: string, worker: LoadedWorker): void;
    (event: "update:modelValue", value: string): void;
    (event: "more"): void;
}>();

// Infinit scroll implementation
const profilContainer = ref();
const moreEmitted = ref(false);
const handleScroll = (event: Event) => {
    if (!profilContainer.value) return;
    const scrollTop = (event.target as HTMLElement).scrollTop + (profilContainer.value as HTMLElement).clientHeight;
    const percent = Math.round((100 * scrollTop) / (profilContainer.value as HTMLElement).scrollHeight);
    if (percent >= 80 && !moreEmitted.value && props.totalItems > props.workers.length && !props.loading) {
        emit("more");
        moreEmitted.value = true;
    }
};

watch(
    () => props.workers,
    () => {
        moreEmitted.value = false;
    }
);

const inputFocused = ref(false);
const handleToggle = (isopen: boolean) => {
    emit("update:modelValue", "");
    inputFocused.value = isopen;
};
</script>
<template>
    <Dropdown max-height="360px" @toggle="handleToggle" close-on-descendant>
        <template #trigger-zone>
            <slot name="trigger-zone"></slot>
        </template>
        <template #dropdown-header>
            <DropdownInput :focused="inputFocused" :modelValue="modelValue" @update:model-value="(value) => emit('update:modelValue', value)" @click.stop />
        </template>
        <template #dropdown-content>
            <div class="avatar-container" ref="profilContainer" @scroll="handleScroll">
                <div class="loading" v-if="loading && workers.length === 0"><Loader /></div>
                <div class="no-result" v-else-if="workers.length === 0">Aucun résultat</div>
                <template v-else>
                    <DropdownItem v-for="worker of workers" @click="() => emit('select', worker.id, worker)" :key="worker.id" flex>
                        <Avatar
                            :image-url="worker.picture"
                            :firstname="worker.firstname"
                            :lastname="worker.lastname"
                            font-weight="500"
                            font-size="13px"
                            :size="AvatarSize.size16"
                            display-name
                        />
                    </DropdownItem>
                    <DropdownItem v-if="loading" label="Chargement..." loading />
                </template>
            </div>
        </template>
    </Dropdown>
</template>
<style scoped lang="scss">
.avatar-container {
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow-y: auto;
    height: calc(100% - 30px);
    .loading {
        display: flex;
        justify-content: center;
    }
}
.no-result {
    padding: 8px;
    text-align: center;
}
</style>
